import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Gavel, ShoppingBag, Palette, Clock, Upload, Loader2, X, Plus, TrendingUp, MessageCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Seo } from "../components/Seo";
import { ChatModal } from "../components/ChatModal";
import { AuctionOrderForm } from "../components/AuctionOrderForm";

const fmt = (amount, currency) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: currency || "EUR" }).format(amount || 0);

function useCountdown(endTime) {
  const [left, setLeft] = useState("");
  const [ended, setEnded] = useState(false);
  useEffect(() => {
    if (!endTime) return;
    const tick = () => {
      const diff = new Date(endTime).getTime() - Date.now();
      if (diff <= 0) {
        setEnded(true);
        setLeft("Terminée");
        return;
      }
      const h = Math.floor(diff / 3.6e6);
      const m = Math.floor((diff % 3.6e6) / 6e4);
      const s = Math.floor((diff % 6e4) / 1000);
      setLeft(`${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endTime]);
  return { left, ended };
}

const Convert = ({ amount, currency, rate }) => {
  if (!rate) return null;
  const conv = currency === "EUR" ? amount * rate.EUR_USD : amount * rate.USD_EUR;
  const target = currency === "EUR" ? "USD" : "EUR";
  return <span className="text-zinc-500 text-sm"> ≈ {fmt(conv, target)}</span>;
};

const AuctionCard = ({ a, rate, onOpen }) => {
  const { left, ended } = useCountdown(a.end_time);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="border border-white/10 bg-[#0d0d0d] cursor-pointer group"
      onClick={() => onOpen(a)}
      data-testid={`auction-card-${a.id}`}
    >
      <div className="aspect-[4/3] overflow-hidden relative">
        <img src={a.image_url} alt={a.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className={`absolute top-3 left-3 px-3 py-1 text-[10px] uppercase tracking-widest ${ended || a.status !== "active" ? "bg-zinc-700 text-white" : "bg-[#E11D48] text-white"}`}>
          {a.status === "paid" ? "Vendue" : ended || a.status !== "active" ? "Terminée" : "En cours"}
        </div>
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-3 flex items-center gap-2 text-white text-sm">
          <Clock className="h-4 w-4 text-[#60a5fa]" /> {left}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-display text-xl text-white">{a.title}</h3>
        <p className="text-zinc-500 text-sm">{a.artist_name}</p>
        <div className="flex items-center justify-between mt-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">Offre actuelle</p>
            <p className="text-white text-lg">{fmt(a.current_price, a.currency)}<Convert amount={a.current_price} currency={a.currency} rate={rate} /></p>
          </div>
          <span className="text-zinc-500 text-xs flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> {a.bid_count}</span>
        </div>
      </div>
    </motion.div>
  );
};

const BidModal = ({ auction, rate, onClose, onRefresh }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { left, ended } = useCountdown(auction.end_time);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const isWinner = user && auction.highest_bidder_id === user.id;
  const isSeller = user && auction.seller_id === user.id;
  const minBid = Number(auction.current_price) + 1;

  const placeBid = async () => {
    if (!user) {
      navigate("/connexion", { state: { from: "/encheres" } });
      return;
    }
    const val = parseFloat(amount);
    if (!val || val <= Number(auction.current_price)) {
      toast.error(`Votre offre doit dépasser ${fmt(auction.current_price, auction.currency)}`);
      return;
    }
    setLoading(true);
    try {
      await api.post(`/auctions/${auction.id}/bid`, { amount: val });
      toast.success("Votre enchère a été enregistrée !");
      onRefresh();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const finished = ended || auction.status !== "active";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bid-modal">
      <div className="bg-[#0d0d0d] border border-white/15 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="font-display text-2xl text-white">{auction.title}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="close-bid-modal"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-6 p-6">
          <img src={auction.image_url} alt={auction.title} className="w-full object-cover aspect-square bg-black" />
          <div>
            <p className="text-zinc-500 text-sm">{auction.artist_name}</p>
            {auction.description && <p className="text-zinc-300 text-sm mt-3 leading-relaxed">{auction.description}</p>}
            <div className="text-sm text-zinc-400 mt-4 space-y-1">
              {auction.technique && <p>Technique : <span className="text-zinc-200">{auction.technique}</span></p>}
              {auction.dimensions && <p>Dimensions : <span className="text-zinc-200">{auction.dimensions}</span></p>}
              <p>Frais de livraison : <span className="text-zinc-200">{fmt(auction.shipping_fee, auction.currency)}</span></p>
            </div>

            <div className="mt-5 p-4 border border-white/10">
              <div className="flex items-center gap-2 text-[#60a5fa] text-sm mb-2"><Clock className="h-4 w-4" /> {left}</div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Offre actuelle</p>
              <p className="text-2xl text-white">{fmt(auction.current_price, auction.currency)}<Convert amount={auction.current_price} currency={auction.currency} rate={rate} /></p>
              {auction.highest_bidder_name && <p className="text-zinc-500 text-xs mt-1">Meilleur enchérisseur : {auction.highest_bidder_name}</p>}
            </div>

            {!finished ? (
              <div className="mt-5">
                <input
                  data-testid="bid-amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Min. ${minBid} ${auction.currency}`}
                  className="w-full bg-black border border-white/15 px-4 py-3 text-white focus:border-[#2563EB] outline-none"
                />
                <button data-testid="place-bid-button" onClick={placeBid} disabled={loading}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white py-3 text-sm transition-colors">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gavel className="h-4 w-4" />} Enchérir
                </button>
                {!user && <p className="text-zinc-500 text-xs mt-2 text-center">Connectez-vous pour enchérir.</p>}
              </div>
            ) : auction.status === "paid" ? (
              <p className="mt-5 text-emerald-400 text-sm">Cette œuvre a été vendue et réglée.</p>
            ) : isWinner ? (
              <AuctionOrderForm
                auction={auction}
                onContact={() => setChatOpen(true)}
                onRefresh={onRefresh}
              />
            ) : (
              <p className="mt-5 text-zinc-400 text-sm">Enchère terminée. {auction.highest_bidder_name ? `Remportée par ${auction.highest_bidder_name}.` : "Aucune offre."}</p>
            )}

            {auction.seller_id && !isSeller && !isWinner && (
              <button
                data-testid="contact-seller-button"
                onClick={() => setChatOpen(true)}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white text-white py-3 text-sm transition-colors"
              >
                <MessageCircle className="h-4 w-4" /> Contacter le vendeur
              </button>
            )}
          </div>
        </div>
      </div>
      {chatOpen && (
        <ChatModal
          receiverId={auction.seller_id}
          receiverName={auction.artist_name}
          contextType="auction"
          contextId={auction.id}
          contextTitle={auction.title}
          onClose={() => setChatOpen(false)}
        />
      )}
    </div>
  );
};

const SellerForm = ({ onCreated }) => {
  const [f, setF] = useState({ title: "", description: "", image_url: "", currency: "EUR", start_price: "", shipping_fee: "", technique: "", dimensions: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, image_url: res.data.image_url }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.image_url || !f.start_price) {
      toast.error("Titre, image et prix de départ sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auctions", {
        ...f, start_price: parseFloat(f.start_price), shipping_fee: parseFloat(f.shipping_fee || 0),
      });
      toast.success("Enchère mise en ligne pour 24h !");
      setF({ title: "", description: "", image_url: "", currency: "EUR", start_price: "", shipping_fee: "", technique: "", dimensions: "" });
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-white/10 bg-[#0d0d0d] p-6 space-y-4" data-testid="seller-auction-form">
      <h3 className="font-display text-2xl text-white">Mettre une œuvre aux enchères</h3>
      <div className="flex gap-4 items-start">
        <div className="h-24 w-24 shrink-0 bg-black border border-white/15 overflow-hidden flex items-center justify-center">
          {f.image_url ? <img src={f.image_url} alt="" className="h-full w-full object-cover" /> : <Plus className="h-6 w-6 text-zinc-700" />}
        </div>
        <div className="flex-1">
          <label className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white px-4 py-2.5 text-sm cursor-pointer transition-colors">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Téléverser l'œuvre
            <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="auction-upload" />
          </label>
          <input data-testid="auction-image-url" value={f.image_url} onChange={up("image_url")} placeholder="ou collez une URL d'image"
            className="w-full bg-black border border-white/15 px-3 py-2 text-white text-sm mt-3 focus:border-[#2563EB] outline-none" />
        </div>
      </div>
      <input data-testid="auction-title" value={f.title} onChange={up("title")} placeholder="Titre de l'œuvre *"
        className="w-full bg-black border border-white/15 px-3 py-2.5 text-white focus:border-[#2563EB] outline-none" />
      <textarea data-testid="auction-description" value={f.description} onChange={up("description")} placeholder="Description" rows={2}
        className="w-full bg-black border border-white/15 px-3 py-2.5 text-white focus:border-[#2563EB] outline-none resize-none" />
      <div className="grid grid-cols-2 gap-4">
        <input data-testid="auction-technique" value={f.technique} onChange={up("technique")} placeholder="Technique"
          className="bg-black border border-white/15 px-3 py-2.5 text-white focus:border-[#2563EB] outline-none" />
        <input data-testid="auction-dimensions" value={f.dimensions} onChange={up("dimensions")} placeholder="Dimensions"
          className="bg-black border border-white/15 px-3 py-2.5 text-white focus:border-[#2563EB] outline-none" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500">Devise</label>
          <select data-testid="auction-currency" value={f.currency} onChange={up("currency")}
            className="w-full bg-black border border-white/15 px-3 py-2.5 text-white mt-1 focus:border-[#2563EB] outline-none">
            <option value="EUR">€ Euro</option>
            <option value="USD">$ Dollar</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500">Prix de départ *</label>
          <input data-testid="auction-start-price" type="number" value={f.start_price} onChange={up("start_price")}
            className="w-full bg-black border border-white/15 px-3 py-2.5 text-white mt-1 focus:border-[#2563EB] outline-none" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500">Frais livraison</label>
          <input data-testid="auction-shipping" type="number" value={f.shipping_fee} onChange={up("shipping_fee")}
            className="w-full bg-black border border-white/15 px-3 py-2.5 text-white mt-1 focus:border-[#2563EB] outline-none" />
        </div>
      </div>
      <p className="text-zinc-500 text-xs">⏱ L'enchère sera active pendant 24 heures à partir de sa publication.</p>
      <button data-testid="create-auction-button" type="submit" disabled={saving}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white py-3 text-sm transition-colors">
        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Lancer l'enchère
      </button>
    </form>
  );
};

export default function Auctions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [auctions, setAuctions] = useState([]);
  const [myAuctions, setMyAuctions] = useState([]);
  const [rate, setRate] = useState(null);
  const [selected, setSelected] = useState(null);

  const loadAuctions = useCallback(() => {
    api.get("/auctions").then((r) => setAuctions(r.data)).catch(() => {});
  }, []);
  const loadMine = useCallback(() => {
    if (user && (user.role === "artist" || user.role === "admin")) {
      api.get("/my/auctions").then((r) => setMyAuctions(r.data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    loadAuctions();
    api.get("/exchange-rate").then((r) => setRate(r.data)).catch(() => {});
  }, [loadAuctions]);
  useEffect(() => { loadMine(); }, [loadMine]);

  return (
    <div className="pt-32 pb-24 max-w-7xl mx-auto px-6 lg:px-10 min-h-screen" data-testid="auctions-page">
      <Seo title="Enchères en Ligne" description="Enchérissez en direct sur des œuvres d'artistes handicapés. Enchères de 24h, paiement sécurisé, livraison incluse." path="/encheres" />
      <p className="text-xs uppercase tracking-[0.3em] text-[#60a5fa] mb-3">Temps réel · 24h</p>
      <h1 className="font-display text-5xl md:text-6xl text-white tracking-tight mb-8">Enchères en Ligne</h1>

      {/* Role choice */}
      <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mb-14">
        <button data-testid="role-buyer" onClick={() => setRole("buyer")}
          className={`flex items-center gap-4 p-6 border text-left transition-all ${role === "buyer" ? "border-[#2563EB] bg-[#2563EB]/10" : "border-white/15 hover:border-white/40"}`}>
          <ShoppingBag className="h-7 w-7 text-[#60a5fa]" />
          <div><p className="text-white font-display text-xl">Je suis acheteur</p><p className="text-zinc-500 text-sm">Enchérir sur les œuvres</p></div>
        </button>
        <button data-testid="role-seller" onClick={() => setRole("seller")}
          className={`flex items-center gap-4 p-6 border text-left transition-all ${role === "seller" ? "border-[#2563EB] bg-[#2563EB]/10" : "border-white/15 hover:border-white/40"}`}>
          <Palette className="h-7 w-7 text-[#60a5fa]" />
          <div><p className="text-white font-display text-xl">Je suis vendeur</p><p className="text-zinc-500 text-sm">Mettre une œuvre aux enchères</p></div>
        </button>
      </div>

      {/* BUYER VIEW */}
      {role === "buyer" && (
        <div data-testid="buyer-view">
          <h2 className="font-display text-3xl text-white mb-8">Œuvres aux enchères</h2>
          {auctions.length === 0 ? (
            <p className="text-zinc-500">Aucune enchère en cours pour le moment.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {auctions.map((a) => <AuctionCard key={a.id} a={a} rate={rate} onOpen={setSelected} />)}
            </div>
          )}
        </div>
      )}

      {/* SELLER VIEW */}
      {role === "seller" && (
        <div data-testid="seller-view">
          {!user ? (
            <div className="border border-white/10 bg-[#0d0d0d] p-8 text-center">
              <p className="text-zinc-300 mb-5">Pour vendre aux enchères, vous devez avoir un compte artiste (nom, IBAN, adresse, photo, email).</p>
              <button onClick={() => navigate("/inscription", { state: { from: "/encheres" } })}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-8 py-3 text-sm transition-colors" data-testid="seller-register-cta">
                Créer un compte artiste
              </button>
            </div>
          ) : user.role === "customer" ? (
            <div className="border border-white/10 bg-[#0d0d0d] p-8 text-center">
              <p className="text-zinc-300">Votre compte est un compte acheteur. Pour vendre, créez un compte artiste avec une autre adresse email.</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-10">
              <SellerForm onCreated={() => { loadMine(); loadAuctions(); }} />
              <div>
                <h3 className="font-display text-2xl text-white mb-5">Mes enchères</h3>
                {myAuctions.length === 0 ? (
                  <p className="text-zinc-500">Aucune enchère publiée.</p>
                ) : (
                  <div className="space-y-4">
                    {myAuctions.map((a) => (
                      <div key={a.id} className="flex gap-4 border border-white/10 bg-[#0d0d0d] p-3" data-testid={`my-auction-${a.id}`}>
                        <img src={a.image_url} alt={a.title} className="h-20 w-20 object-cover" />
                        <div className="flex-1">
                          <p className="text-white">{a.title}</p>
                          <p className="text-zinc-400 text-sm">{fmt(a.current_price, a.currency)} · {a.bid_count} offre{a.bid_count > 1 ? "s" : ""}</p>
                          <span className={`text-xs uppercase ${a.status === "active" ? "text-[#60a5fa]" : a.status === "paid" ? "text-emerald-400" : "text-zinc-500"}`}>
                            {a.status === "active" ? "En cours" : a.status === "paid" ? "Vendue & payée" : "Terminée"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {selected && <BidModal auction={selected} rate={rate} onClose={() => setSelected(null)} onRefresh={() => { loadAuctions(); api.get(`/auctions/${selected.id}`).then((r) => setSelected(r.data)).catch(() => {}); }} />}
    </div>
  );
}
